'use client';
import { RiTwitterXLine } from 'react-icons/ri';
import toast from 'react-hot-toast';

import { ListingPayload } from '@/slw/entities/seller/listing';
import { Button } from '@/slw/shared/ui/button';
import { cn, unsecuredCopyToClipboard } from '@/slw/shared/lib/utils';
import { BASE_URL } from '@/slw/shared/consts/common.const';
import { FacebookDebug } from './facebook-debug';

// Facebook SDK types
declare global {
  interface Window {
    FB: {
      ui: (params: {
        method: string;
        href: string;
        display: string;
      }, callback?: (response: any) => void) => void;
    };
  }
}

interface ListingSidebarProps {
  data: ListingPayload;
  onRedirectEmail?: () => void;
  onRedirectTwitter?: () => void;
  onRedirectMessenger?: () => void;
  onRedirectConversation?: () => void;
  onRedirectSimilarVehicles?: () => void;
  isLoading?: boolean;
  disableMessageSellerBtn?: boolean;
}

export function ListingSidebar({
  data,
  onRedirectEmail,
  onRedirectTwitter,
  onRedirectMessenger,
  onRedirectConversation,
  onRedirectSimilarVehicles,
  isLoading,
  disableMessageSellerBtn,
}: ListingSidebarProps) {
  const handleCopy = () => {
    if (window.isSecureContext && navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied successfully');
    } else {
      const url = `${BASE_URL}/listing/${data.url}`;

      unsecuredCopyToClipboard(url);
      toast.success('Link copied successfully');
    }
  };

  return (
    <aside className='w-full lg:w-[437px] flex-shrink-0 flex flex-col gap-12'>
      {/* Price & Overview */}
      <div className='bg-[#F8F8F7] rounded-md p-4 flex flex-col gap-2'>
        <h3 className='text-2xl font-bold'>Price</h3>
        <h2 className='text-[2rem] font-bold text-primary'>
          ${data.price?.toLocaleString()} {data && data.orBestOffer && <span>(OBO)</span>}
        </h2>
      </div>
      <div className='flex flex-col gap-4'>
        <h3 className='text-2xl font-bold'>Overview</h3>
        <p className='text-sm font-medium'>Listing ID: {data.listingId}</p>
        <div className='bg-[#F8F8F7] rounded-md py-6 px-4 tracking-normal text-sm font-normal leading-[150%] '>
          {data.shortVehicleDescription}
        </div>
      </div>
      {/* Actions */}
      <Button className='w-full gap-2' onClick={onRedirectConversation} disabled={isLoading || disableMessageSellerBtn}>
        MESSAGE SELLER!
        <img src='/images/icon/conversation.svg' alt='conversation' />
      </Button>
      <div className='flex flex-col gap-4'>
        <p className='text-sm font-medium'>Interested in something else?</p>
        <Button variant='outline' className='w-full gap-2' onClick={onRedirectSimilarVehicles} disabled={isLoading}>
          SIMILAR VEHICLES
          <img src='/images/icon/car.svg' alt='truck' />
        </Button>
      </div>

      {/* Share section */}
      <div className='flex flex-col gap-4'>
        <p className='text-sm font-medium'>Share with a friend</p>
        <div className='flex gap-8 items-center '>
          {/* Copy link */}
          <div className={cn('flex flex-col items-center gap-2')}>
            <button type='button' className='cursor-pointer size-8' onClick={handleCopy} aria-label='Copy link'>
              <img src='/images/icon/copy-link.svg' alt='copy' />
            </button>
            <span className='text-sm'>{'Copy link'}</span>
          </div>
          {/* X (Twitter) */}
          <div className={cn('flex flex-col items-center gap-2')}>
            <button
              type='button'
              className='cursor-pointer size-8'
              onClick={() => {
                const url = encodeURIComponent(window.location.href);
                const text = encodeURIComponent(`Check out this listing: ${data.title || 'Amazing vehicle'}`);
                const hashtags = encodeURIComponent('#VehicleListing #ClovisClassics');
                window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}&hashtags=${hashtags}`, '_blank');
              }}
              aria-label='Share on X'
            >
              <RiTwitterXLine size={32} />
            </button>
            <span className='text-sm'>x</span>
          </div>
          {/* Gmail */}
          <div className={cn('flex flex-col items-center gap-2')}>
            <button
              type='button'
              className='cursor-pointer size-8'
              onClick={() => {
                if (isLoading) handleCopy();

                onRedirectEmail?.();
              }}
              aria-label='Share via Gmail'
            >
              <img src='/images/icon/gmail.svg' alt='gmail' />
            </button>
            <span className='text-sm'>Gmail</span>
          </div>
          {/* Facebook */}
          <div className={cn('flex flex-col items-center gap-2')}>
            <button
              type='button'
              className='cursor-pointer size-8'
              onClick={() => {
                const url = window.location.href;
                
                // Sử dụng Facebook Share Dialog nếu có SDK
                if (window.FB) {
                  window.FB.ui({
                    method: 'share',
                    href: url,
                    display: 'popup'
                  }, (response) => {
                    if (response && !response.error_message) {
                      // Facebook share successful
                    }
                  });
                } else {
                  // Fallback: Mở Facebook share
                  window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
                }
              }}
              aria-label='Share via Facebook'
            >
              <svg className="w-8 h-8" fill="#1877F2" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </button>
            <span className='text-sm'>Facebook</span>
          </div>
        </div>
      </div>

      {/* Facebook Debug Component - Remove in production */}
      {process.env.NODE_ENV === 'development' && (
        <div className="mt-6">
          <FacebookDebug
            url={window.location.href}
            title={data.title || 'Vehicle Listing'}
            description={data.shortVehicleDescription || 'Check out this amazing vehicle listing'}
            imageUrl={data.mainImageUrl || `${process.env.NEXT_PUBLIC_BASE_URL || 'https://ecommerce.slwtechnology.com'}/images/logo/logo.png`}
          />
        </div>
      )}
    </aside>
  );
}
